import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft, Upload, X, MapPin } from 'lucide-react';
import LocationMap from './LocationMap';
import ImageCropper from './ImageCropper';
import { useAuth } from '../contexts/AuthContext';
import { computeIdadeFromDataNascimento } from '../utils/age';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function DateInput({ value, onChange, className = '', required = false }) {
  const toDisplay = (iso) => {
    if (!iso) return '';
    const p = iso.slice(0, 10).split('-');
    return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
  };
  const toIso = (display) => {
    const p = display.replace(/[^\d/]/g, '').split('/');
    if (p.length === 3 && p[0].length === 2 && p[1].length === 2 && p[2].length === 4) return `${p[2]}-${p[1]}-${p[0]}`;
    return '';
  };
  const [text, setText] = useState(toDisplay(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setText(toDisplay(value)); }, [value, focused]);
  const handleChange = (e) => {
    let v = e.target.value.replace(/[^\d/]/g, '');
    if (v.length === 2 && !v.includes('/')) v += '/';
    if (v.length === 5 && v.split('/').length === 2) v += '/';
    if (v.length > 10) v = v.slice(0, 10);
    setText(v);
    const iso = toIso(v);
    if (iso) onChange(iso);
    else if (v === '') onChange('');
  };
  const isoForPicker = value && value.length >= 10 ? value.slice(0, 10) : '';
  return (
    <div className={`relative inline-flex items-center w-full ${className}`}>
      <input value={text} onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder="dd/mm/aaaa" maxLength={10} required={required}
        className="flex-1 rounded-xl border border-border bg-background px-3 py-2 pr-9 text-sm focus:ring-2 focus:ring-primary focus:outline-none" />
      <input type="date" value={isoForPicker}
        onChange={e => { if (e.target.value) { onChange(e.target.value); setText(toDisplay(e.target.value)); } }}
        className="absolute right-1 top-1/2 -translate-y-1/2 w-7 h-7 opacity-0 cursor-pointer"
        aria-label="Selecionar data no calendário" />
      <svg className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none"
        fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    </div>
  );
}
const BEHAVIOR_COLORS_OPT = [{ value: 'verde', label: 'Verde (Calmo)', color: '#4CAF50' }, { value: 'amarelo', label: 'Amarelo (Atenção)', color: '#FFC107' }, { value: 'vermelho', label: 'Vermelho (Perigoso)', color: '#F44336' }];
const MOTIVOS_ENTRADA = ['Entrega por titular/detentor', 'Entrega por achador', 'Recolha de animal errante', 'Sequestro', 'Recolha ao domicílio'];

export default function AnimalForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOp = user?.role === 'operacional';
  const [boxes, setBoxes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fotos, setFotos] = useState([]); // File[] staged before create
  const [cropperFiles, setCropperFiles] = useState(null);
  const [form, setForm] = useState({
    nome: '', especie: 'canina', chip: '', num_processo: '', sexo: '', idade: '', raca: '', cor: '', peso: '',
    data_nascimento: '', entrada: new Date().toISOString().slice(0, 10), box: '',
    cor_comportamento: 'verde', esterilizado: false, data_esterilizacao: '', titular: false, dados_titular: '',
    tribunal: false, dados_tribunal: '',
    data_transferencia_cro: '', motivo_entrada: '', local_ocorrencia: '', num_porta: '', codigo_postal: '', freguesia: '', concelho: '', disponivel_adocao: false,
    observacoes: '', desparasitado: false, produto_desparasitacao: '', data_desparasitacao: '',
    entrada_doacao: false, obs_administrativa: ''
  });

  useEffect(() => {
    axios.get(`${API}/boxes/nomes`).then(r => setBoxes(r.data)).catch(() => {});
    if (id) {
      axios.get(`${API}/animais/${id}`).then(r => {
        const d = r.data;
        // Recompute idade dynamically from data_nascimento (stale stored value would keep showing old age)
        const idadeCalculada = d.data_nascimento ? computeIdadeFromDataNascimento(d.data_nascimento) : (d.idade || '');
        setForm(prev => ({ ...prev, ...d, idade: idadeCalculada, esterilizado: !!d.esterilizado, titular: !!d.titular, tribunal: !!d.tribunal, disponivel_adocao: !!d.disponivel_adocao, desparasitado: !!d.desparasitado, entrada_doacao: !!d.entrada_doacao }));
      }).catch(console.error);
    }
  }, [id]);

  const [chipError, setChipError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.chip && form.chip.length !== 15) {
      setChipError('O chip deve ter exactamente 15 dígitos');
      return;
    }
    setChipError('');
    setLoading(true);
    try {
      let animalId = id;
      if (id) {
        await axios.put(`${API}/animais/${id}`, form);
      } else {
        const r = await axios.post(`${API}/animais`, form);
        animalId = r.data.id;
      }
      // Upload staged photos (works both on creation AND edit)
      if (fotos.length > 0 && animalId) {
        // Compress one by one and upload individually so we never hit ingress limits
        const errors = [];
        let okCount = 0;
        for (const original of fotos) {
          try {
            const { compressImageFile } = await import('../utils/imageCompress');
            const compressed = await compressImageFile(original);
            const fd = new FormData();
            fd.append('file', compressed);
            await axios.post(`${API}/animais/${animalId}/fotos`, fd, {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 180000,
            });
            okCount++;
          } catch (e) {
            errors.push({ filename: original.name, error: e.response?.data?.detail || e.message || 'Erro' });
          }
        }
        if (errors.length > 0) {
          alert(`${okCount} de ${fotos.length} foto(s) carregadas.\nFalharam:\n${errors.map(x => `• ${x.filename}: ${x.error}`).join('\n')}`);
        }
      }
      navigate('/');
    } catch (err) {
      console.error(err);
      const detail = err.response?.data?.detail || err.message || 'Erro desconhecido';
      alert(`Erro ao guardar: ${detail}`);
    }
    finally { setLoading(false); }
  };

  const set = (field, value) => {
    setForm(f => {
      const updated = { ...f, [field]: value };
      if (field === 'data_nascimento' && value) {
        const birth = new Date(value);
        const now = new Date();
        let years = now.getFullYear() - birth.getFullYear();
        let months = now.getMonth() - birth.getMonth();
        if (now.getDate() < birth.getDate()) months--;
        if (months < 0) { years--; months += 12; }
        if (years < 0) { years = 0; months = 0; }
        if (years < 1) {
          const totalMonths = Math.max(0, months);
          updated.idade = `${totalMonths} ${totalMonths === 1 ? 'mês' : 'meses'}`;
        } else {
          updated.idade = `${years} ${years === 1 ? 'ano' : 'anos'}`;
        }
      }
      return updated;
    });
  };
  const setChip = (v) => {
    const digits = v.replace(/\D/g, '').slice(0, 15);
    set('chip', digits);
    if (digits.length > 0 && digits.length !== 15) setChipError(`${digits.length}/15 dígitos`);
    else setChipError('');
  };
  const showDoacao = form.motivo_entrada === 'Entrega por titular/detentor';
  const showSequestroDoacao = form.motivo_entrada === 'Sequestro' || form.motivo_entrada === 'Recolha ao domicílio';
  const showTitularTransfer = form.titular && form.chip;

  // Geocoding preview — debounced on local_ocorrencia / concelho change
  const [geoPreview, setGeoPreview] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const fetchGeocode = useCallback(async (local, concelho, num_porta, codigo_postal, freguesia) => {
    if (!local || local.trim().length < 3) { setGeoPreview(null); return; }
    setGeoLoading(true);
    try {
      const r = await axios.get(`${API}/animais/geocoding/search`, { params: { q: local, concelho, num_porta, codigo_postal, freguesia } });
      if (r.data && r.data.lat) {
        setGeoPreview(r.data);
        setForm(f => ({ ...f,
          local_ocorrencia_lat: r.data.lat,
          local_ocorrencia_lng: r.data.lng,
          local_ocorrencia_display: r.data.display_name,
        }));
      } else {
        setGeoPreview(null);
      }
    } catch (e) { setGeoPreview(null); }
    finally { setGeoLoading(false); }
  }, []);
  useEffect(() => {
    const handle = setTimeout(() => fetchGeocode(form.local_ocorrencia, form.concelho, form.num_porta, form.codigo_postal, form.freguesia), 600);
    return () => clearTimeout(handle);
  }, [form.local_ocorrencia, form.concelho, form.num_porta, form.codigo_postal, form.freguesia, fetchGeocode]);

  return (
    <div data-testid="animal-form-page">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/')} className="p-2 rounded-full hover:bg-muted transition-colors"><ArrowLeft className="w-5 h-5" /></button>
        <h2 className="font-heading font-bold text-2xl">{id ? 'Editar Animal' : 'Adicionar Animal'}</h2>
      </div>
      {isOp ? (
        <form onSubmit={handleSubmit} className="bg-card rounded-2xl border border-border p-6 space-y-6">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
            Preenche os campos essenciais. Os técnicos completam o resto da ficha mais tarde.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Field label="Nome / Nº de Processo *" value={form.nome} onChange={v => set('nome', v)} required testid="form-nome" />
            <div><label className="text-sm font-medium block mb-1">Data Entrada *</label><DateInput value={form.entrada} onChange={v => set('entrada', v)} className="w-full" required /></div>
            <Sel label="Espécie *" value={form.especie} onChange={v => set('especie', v)} options={[['canina','Canina'],['felina','Felina']]} testid="form-especie" />
            <Sel label="Motivo de Entrada *" value={form.motivo_entrada} onChange={v => set('motivo_entrada', v)} options={MOTIVOS_ENTRADA.map(m => [m, m])} testid="form-motivo" />
            <Sel label="Sexo" value={form.sexo} onChange={v => set('sexo', v)} options={[['macho','Macho'],['fêmea','Fêmea']]} testid="form-sexo" />
            <Field label="Cor" value={form.cor} onChange={v => set('cor', v)} testid="form-cor" />
            <div>
              <Field label="Chip (15 dígitos)" value={form.chip} onChange={setChip} testid="form-chip" />
              {chipError && <p className="text-xs text-destructive mt-1">{chipError}</p>}
            </div>
            <Sel label="Box" value={form.box} onChange={v => set('box', v)} options={[['', 'Sem box'], ...boxes.map(b => [b, b])]} testid="form-box" />
            <Field label="Local de Ocorrência" value={form.local_ocorrencia} onChange={v => set('local_ocorrencia', v)} testid="form-local" placeholder="Rua, freguesia, concelho" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-lg mb-2">Fotos do Animal {id ? '(adicionar mais)' : ''}</h3>
            <div className="flex flex-wrap gap-3 items-center">
              <label className="flex items-center gap-2 bg-muted hover:bg-border transition-all rounded-xl border border-border px-4 py-3 cursor-pointer text-sm">
                <Upload className="w-4 h-4" /> Adicionar Fotos
                <input type="file" accept="image/*" multiple className="hidden"
                  onChange={e => {
                    const files = Array.from(e.target.files || []);
                    if (files.length > 0) setCropperFiles(files);
                    e.target.value = '';
                  }} />
              </label>
              {fotos.length > 0 && <span className="text-xs text-muted-foreground">{fotos.length} foto{fotos.length > 1 ? 's' : ''}</span>}
            </div>
            {fotos.length > 0 && (
              <div className="flex flex-wrap gap-3 mt-3">
                {fotos.map((f, i) => (
                  <div key={i} className="relative w-24 h-24 rounded-lg overflow-hidden border border-border bg-background">
                    <img src={URL.createObjectURL(f)} alt={f.name} className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setFotos(prev => prev.filter((_, idx) => idx !== i))}
                      className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5 hover:bg-black/80">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Observações</label>
            <textarea value={form.observacoes || ''} onChange={e => set('observacoes', e.target.value)} rows={3}
              placeholder="Notas relevantes sobre o animal..."
              className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" />
          </div>
          <button type="submit" disabled={loading} data-testid="form-submit"
            className="bg-primary text-primary-foreground rounded-full px-6 py-3 font-medium hover:brightness-110 transition-all flex items-center gap-2 disabled:opacity-50">
            <Save className="w-4 h-4" /> {loading ? 'A guardar…' : (id ? 'Guardar Alterações' : 'Registar Animal')}
          </button>
        </form>
      ) : (
      <form onSubmit={handleSubmit} className="bg-card rounded-2xl border border-border p-6 space-y-6">
        <h3 className="font-heading font-bold text-lg">Dados Obrigatórios</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <Field label="Nome / Nº de Processo *" value={form.nome} onChange={v => set('nome', v)} required testid="form-nome" />
          <Sel label="Espécie *" value={form.especie} onChange={v => set('especie', v)} options={[['canina','Canina'],['felina','Felina']]} testid="form-especie" />
          <Sel label="Motivo de Entrada *" value={form.motivo_entrada} onChange={v => set('motivo_entrada', v)} options={MOTIVOS_ENTRADA.map(m => [m, m])} testid="form-motivo" />
          <Sel label="Sexo *" value={form.sexo} onChange={v => set('sexo', v)} options={[['macho','Macho'],['fêmea','Fêmea']]} testid="form-sexo" />
          <Field label="Idade" value={form.idade} onChange={v => set('idade', v)} testid="form-idade" placeholder="ex: 3 anos / 6 meses" />
          <div><label className="text-sm font-medium block mb-1">Data Nascimento</label><DateInput value={form.data_nascimento} onChange={v => set('data_nascimento', v)} className="w-full" /></div>
          <div><label className="text-sm font-medium block mb-1">Data Entrada *</label><DateInput value={form.entrada} onChange={v => set('entrada', v)} className="w-full" required /></div>
          <div className="sm:col-span-2 md:col-span-3 flex items-center gap-6 pt-1">
            <label className="flex items-center gap-2 text-sm font-medium" data-testid="form-esterilizado-lbl">
              <input type="checkbox" checked={form.esterilizado} onChange={e => set('esterilizado', e.target.checked)} data-testid="form-esterilizado" className="rounded" />
              Esterilizado *
            </label>
            {form.esterilizado && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Data:</span>
                <DateInput value={form.data_esterilizacao} onChange={v => set('data_esterilizacao', v)} className="w-40" />
              </div>
            )}
          </div>
        </div>

        {showDoacao && (
          <div className="bg-accent/10 rounded-xl p-3">
            <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={form.entrada_doacao} onChange={e => set('entrada_doacao', e.target.checked)} className="rounded" />Esta entrada é uma doação?</label>
            <p className="text-xs text-muted-foreground mt-1">Se sim, não contabiliza como novo animal nas estatísticas.</p>
          </div>
        )}

        {showSequestroDoacao && (
          <div className="bg-purple-50 rounded-xl p-3">
            <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={form.doacao_sequestro || false} onChange={e => set('doacao_sequestro', e.target.checked)} className="rounded" />Animal de sequestro — Houve doação?</label>
            <p className="text-xs text-muted-foreground mt-1">Assinale se houve doação por parte do titular. Contabiliza para estatísticas de doações.</p>
          </div>
        )}

        <h3 className="font-heading font-bold text-lg">Dados Adicionais</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <label className="text-sm font-medium block mb-1">Chip (15 dígitos)</label>
            <input data-testid="form-chip" value={form.chip || ''} onChange={e => setChip(e.target.value)} maxLength={15} inputMode="numeric"
              className={`w-full rounded-xl border bg-background px-3 py-2 text-sm focus:ring-2 focus:outline-none ${chipError ? 'border-red-400 focus:ring-red-300' : 'border-border focus:ring-primary'}`} />
            {chipError && <p className="text-xs text-red-500 mt-0.5">{chipError}</p>}
          </div>
          <Field label="Raça" value={form.raca} onChange={v => set('raca', v)} testid="form-raca" />
          <Field label="Cor" value={form.cor} onChange={v => set('cor', v)} testid="form-cor" />
          <Field label="Peso (kg)" value={form.peso} onChange={v => set('peso', v)} testid="form-peso" inputMode="decimal" />
          <div>
            <label className="text-sm font-medium block mb-1">Box</label>
            <select data-testid="form-box" value={form.box} onChange={e => set('box', e.target.value)} className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:outline-none">
              <option value="">Sem box</option>
              {boxes.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Cor Comportamento</label>
            <div className="flex gap-2">
              {BEHAVIOR_COLORS_OPT.map(c => (
                <button key={c.value} type="button" onClick={() => set('cor_comportamento', c.value)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-all ${form.cor_comportamento === c.value ? 'border-foreground bg-muted' : 'border-border'}`}>
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: c.color }} />{c.label}
                </button>
              ))}
            </div>
          </div>
          <Field label="Local de Ocorrência" value={form.local_ocorrencia} onChange={v => set('local_ocorrencia', v)} testid="form-local" />
          <Field label="Nº de Porta" value={form.num_porta} onChange={v => set('num_porta', v)} testid="form-num-porta" />
          <Field label="Código Postal" value={form.codigo_postal} onChange={v => set('codigo_postal', v)} testid="form-codigo-postal" placeholder="ex: 2600-131" />
          <Field label="Freguesia" value={form.freguesia} onChange={v => set('freguesia', v)} testid="form-freguesia" />
          <Field label="Concelho" value={form.concelho} onChange={v => set('concelho', v)} testid="form-concelho" />
          <label className="flex items-center gap-2 text-sm self-end pb-1">
            <input type="checkbox" checked={form.disponivel_adocao} onChange={e => set('disponivel_adocao', e.target.checked)} data-testid="form-disponivel" className="rounded" />
            Disponível para Adoção
          </label>
        </div>

        {/* Geocoding preview */}
        {(form.local_ocorrencia && form.local_ocorrencia.length >= 3) && (
          <div data-testid="form-geo-preview" className="bg-muted/30 rounded-xl border border-border p-3">
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">Pré-visualização da Localização</span>
              {geoLoading && <span className="text-xs text-muted-foreground">a procurar...</span>}
              {!geoLoading && !geoPreview && <span className="text-xs text-amber-600">Sem resultados — verifique o endereço e concelho</span>}
            </div>
            {geoPreview && (
              <>
                <LocationMap lat={geoPreview.lat} lng={geoPreview.lng} label={geoPreview.display_name} height={220} />
                <p className="text-xs text-muted-foreground mt-1 italic">{geoPreview.display_name}</p>
              </>
            )}
          </div>
        )}

        <h3 className="font-heading font-bold text-lg">Titular</h3>
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.titular} onChange={e => set('titular', e.target.checked)} className="rounded" />Tem Titular</label>
          {form.titular && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Dados do Titular" value={form.dados_titular} onChange={v => set('dados_titular', v)} />
              {showTitularTransfer && <Field label="Data Transferência para CRO" value={form.data_transferencia_cro} onChange={v => set('data_transferencia_cro', v)} type="date" />}
            </div>
          )}
        </div>

        <h3 className="font-heading font-bold text-lg">Tribunal</h3>
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input data-testid="form-tribunal" type="checkbox" checked={form.tribunal} onChange={e => set('tribunal', e.target.checked)} className="rounded" />
            Animal envolvido em processo de Tribunal
          </label>
          {form.tribunal && (
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="text-sm font-medium block mb-1">Dados do Tribunal / Processo</label>
                <textarea data-testid="form-dados-tribunal" rows={2} value={form.dados_tribunal || ''}
                  onChange={e => set('dados_tribunal', e.target.value)}
                  placeholder="Nº processo, tribunal, contacto, observações..."
                  className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:outline-none" />
              </div>
            </div>
          )}
        </div>

        <div>
          <label className="text-sm font-medium block mb-1">Observações</label>
          <textarea data-testid="form-obs" rows={3} value={form.observacoes} onChange={e => set('observacoes', e.target.value)}
            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:outline-none" />
        </div>

        <div>
          <h3 className="font-heading font-bold text-lg mb-2">Fotos do Animal {id ? '(adicionar mais)' : ''}</h3>
          <div className="flex flex-wrap gap-3 items-center">
            <label data-testid="form-upload-fotos" className="flex items-center gap-2 bg-muted hover:bg-border transition-all rounded-xl border border-border px-4 py-3 cursor-pointer text-sm">
              <Upload className="w-4 h-4" /> Adicionar Fotos
              <input type="file" accept="image/*" multiple className="hidden"
                onChange={e => {
                  const files = Array.from(e.target.files || []);
                  if (files.length > 0) setCropperFiles(files);
                  e.target.value = '';
                }} />
            </label>
            {fotos.length > 0 && <span className="text-xs text-muted-foreground">{fotos.length} foto{fotos.length > 1 ? 's' : ''} pronta{fotos.length > 1 ? 's' : ''} para upload ao guardar</span>}
          </div>
          {fotos.length > 0 && (
            <div className="flex flex-wrap gap-3 mt-3">
              {fotos.map((f, i) => (
                <div key={i} className="relative w-24 h-24 rounded-lg overflow-hidden border border-border bg-background">
                  <img src={URL.createObjectURL(f)} alt={f.name} className="w-full h-full object-cover" />
                  <button type="button" data-testid={`remove-foto-${i}`}
                    onClick={() => setFotos(prev => prev.filter((_, idx) => idx !== i))}
                    className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5 hover:bg-black/80">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {id && (
            <p className="text-xs text-muted-foreground mt-2">💡 Para definir a foto de capa, gerir as fotos existentes ou ver a galeria, abre a ficha do animal e vai ao separador Ficheiros.</p>
          )}
        </div>

        <button type="submit" disabled={loading} data-testid="form-submit"
          className="bg-primary text-primary-foreground rounded-full px-6 py-2.5 text-sm font-medium hover:brightness-110 transition-all flex items-center gap-2 disabled:opacity-50">
          <Save className="w-4 h-4" />{loading ? 'A guardar...' : id ? 'Atualizar' : 'Adicionar Animal'}
        </button>
      </form>
      )}
      {cropperFiles && (
        <ImageCropper files={cropperFiles}
          onCancel={() => setCropperFiles(null)}
          onDone={(adjusted) => { setFotos(prev => [...prev, ...adjusted]); setCropperFiles(null); }} />
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', required = false, testid, inputMode, placeholder }) {
  return (
    <div>
      <label className="text-sm font-medium block mb-1">{label}</label>
      <input data-testid={testid} type={type} inputMode={inputMode} placeholder={placeholder} value={value || ''} onChange={e => onChange(e.target.value)} required={required}
        className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:outline-none" />
    </div>
  );
}

function Sel({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-sm font-medium block mb-1">{label}</label>
      <select data-testid={testid} value={value} onChange={e => onChange(e.target.value)}
        className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:outline-none">
        <option value="">— Selecionar —</option>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
